import React, {
  useRef,
  useState,
  useContext,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import {StyleSheet, PixelRatio, Pressable, View, Modal} from 'react-native';
import {withGlobalize, WithGlobalizeProps} from 'src/i18n/HOC/withGlobalize';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import BottomSheet, {
  BottomSheetBackdropProps,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import ScreenContext from '../../../ScreenContext';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import Styles from 'src/components/Styles';
import Localized from 'src/constants/AppStrings';
import {Platform} from 'react-native';
import AVTouchableOpacity from 'src/components/elements/AVTouchableOpacity';
import CancelIcon from 'src/components/img/svg/CancelIcon';
import AVText from 'src/components/elements/AVText';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import {getDescriber} from 'src/components/screens/orderAhead/descriptor/DescriptorType';
import Settings from 'src/Settings';
import SuccessTickIcon from 'src/components/img/svg/SuccessTickIcon';
import AlertCircleExclamation from 'src/components/img/AlertCircleExclamation';
import ActionsFactory from 'src/actions/ActionsFactory';
import AccountStore from 'src/stores/AccountStore';
import {useAppDispatch} from 'src/redux/hooks';
import {fetchAccount} from 'src/redux/slices/accountSlice';
import useKeyboard from 'src/components/screens/rewards/phoneNumber/KeyboardHandler';
import CustomTextInput from 'src/components/elements/CustomTextInput';

export type VerifyPhoneNumberScreenProps = WithGlobalizeProps & {
  rewardPolicyId?: string;
  phoneNumber: string;
  countryCode: string;
  fromRewards: boolean;
};

enum BottomSheetState {
  NOT_READY = 'NOT_READY',
  FULLY_OPEN = 'FULLY_OPEN',
  FULLY_CLOSED = 'FULLY_CLOSED',
}

enum ContentTypes {
  VERIFY_PHONE_NUMBER = 'VERIFY_PHONE_NUMBER',
  CLOSE_CONFIRMATION = 'CLOSE_CONFIRMATION',
  VERIFICATION_SUCCESS = 'VERIFICATION_SUCCESS',
}

const VerifyPhoneNumber: React.FC<VerifyPhoneNumberScreenProps> = (props) => {
  const context = useContext(ScreenContext);
  const dispatch = useAppDispatch();

  const {getPickupLocationStyles, getActiveButtonColor} = getDescriber();

  const bottomSheetRef = useRef<BottomSheet>(null);
  const [onCloseCalled, setOnCloseCalled] = useState(false);
  const [_bottomSheetCurrentState, setBottomSheetCurrentState] =
    useState<BottomSheetState>(BottomSheetState.NOT_READY);
  const [modalVisible, setModalVisible] = useState(Platform.OS === 'web');

  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState(null);
  const [isValidOtp, setIsOtpValid] = useState(false);
  const [contentType, setContentType] = useState<ContentTypes>(
    ContentTypes.VERIFY_PHONE_NUMBER,
  );
  const keyBoardHeight = useKeyboard();

  const bottomSheetCustomStates = useMemo(
    () => ({
      [-1]: BottomSheetState.FULLY_CLOSED,
      0: BottomSheetState.FULLY_OPEN,
      999: BottomSheetState.NOT_READY,
    }),
    [],
  );

  useEffect(() => {
    FirebaseAnalytic.trackEvent('componentDidMount', 'VerifyPhoneNumber', {
      ...props,
    });
    if (bottomSheetRef.current) {
      bottomSheetRef.current.close();
    }
  }, [props]);

  const handleOTPChange = (newValue: string) => {
    setOtpValue(newValue);
    setIsOtpValid(newValue.length === 6);
    setOtpError(null);
  };

  const setOtpErrorAction = useCallback(() => {
    setOtpError(Localized.Errors.invalid_otp);
    setIsOtpValid(false);
  }, []);

  const handleVerify = useCallback(async () => {
    context.actions.showSpinner();
    try {
      const res = await ActionsFactory.getAccountActions().verifySmsCodeAction(
        otpValue,
      );
      if (!res.hasOwnProperty('statusCode')) {
        setContentType(ContentTypes.VERIFICATION_SUCCESS);
        await dispatch(fetchAccount(AccountStore.getAccountId()));
        await ActionsFactory.getAccountActions().loadRewardStatus(
          AccountStore.getAccountId(),
        );
      } else {
        setOtpErrorAction();
      }
    } catch (err) {
      setOtpErrorAction();
    } finally {
      context.actions.hideSpinner();
    }
  }, [otpValue, otpError, isValidOtp]);

  const handleResendCode = useCallback(async () => {
    context.actions.showSpinner();
    try {
      const phone = props.countryCode + props.phoneNumber.replaceAll('-', '');
      await ActionsFactory.getAccountActions().sendSmsCodeAction(phone);
      setOtpError(null);
    } catch (err) {
      FirebaseAnalytic.trackEvent(
        'handleResendCode: Error',
        'VerifyPhoneNumber',
        {
          error: err,
        },
      );
    } finally {
      context.actions.hideSpinner();
    }
  }, []);

  const handleDone = () => {
    onClose();
    props.fromRewards
      ? NavActions.reset(AppRoutes.RewardsScreen, {
          refreshRewards: true,
        })
      : NavActions.reset(AppRoutes.ManageAccount);
  };

  const handleLeave = () => {
    onClose();
    NavActions.popToRoute(
      props.fromRewards ? AppRoutes.Rewards : AppRoutes.ManageAccount,
    );
  };

  const handleStayAndVerify = () => {
    setContentType(ContentTypes.VERIFY_PHONE_NUMBER);
  };

  const handleClose = () => {
    if (contentType === ContentTypes.VERIFY_PHONE_NUMBER) {
      setContentType(ContentTypes.CLOSE_CONFIRMATION);
    } else if (contentType === ContentTypes.CLOSE_CONFIRMATION) {
      return;
    } else if (contentType === ContentTypes.VERIFICATION_SUCCESS) {
      onClose();
    }
  };

  const onClose = useCallback(() => {
    if (onCloseCalled) {
      return;
    }
    setOnCloseCalled(true);

    FirebaseAnalytic.trackEvent('onClose', 'VerifyPhoneNumber', {
      ...props,
      navigate: props.fromRewards ? AppRoutes.Rewards : AppRoutes.ManageAccount,
    });

    if (Platform.OS === 'web') {
      setModalVisible(false);
    } else if (bottomSheetRef.current) {
      bottomSheetRef.current.forceClose();
    }

    NavActions.pop();
  }, [onCloseCalled, props]);

  const handleSheetChange = (index: number) => {
    if (index === -1 && contentType === ContentTypes.VERIFY_PHONE_NUMBER) {
      setContentType(ContentTypes.CLOSE_CONFIRMATION);
      bottomSheetRef.current.expand();
      return;
    }
    setBottomSheetCurrentState(bottomSheetCustomStates[index]);
    if (index === -1) {
      onClose();
    }
  };

  const renderBackdropComponent = (backDropProps: BottomSheetBackdropProps) => (
    <Pressable style={backDropProps.style} onPress={handleClose} />
  );

  const renderContent = () => {
    if (contentType === ContentTypes.VERIFY_PHONE_NUMBER) {
      return verifyPhoneNumberView;
    } else if (contentType === ContentTypes.CLOSE_CONFIRMATION) {
      return closeConfirmationView;
    } else if (contentType === ContentTypes.VERIFICATION_SUCCESS) {
      return verificationSuccess;
    }
  };

  const verifyPhoneNumberView = useMemo(
    () => (
      <View>
        <View style={styles.content}>
          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
            style={styles.headerText}
          >
            {Localized.Labels.enter_code_otp}
          </AVText>
          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
            style={styles.descriptionText}
          >
            {Localized.Labels.formatString(
              Localized.Labels.We_sent_a_six_digit_code,
              props.phoneNumber,
            )}
          </AVText>
          <View style={styles.inputContainer}>
            <CustomTextInput
              keyboardType="numeric"
              value={otpValue}
              mode="outlined"
              accessible={true}
              label={Localized.Labels.enter_code_otp}
              accessibilityLabel={Localized.Labels.enter_code_otp}
              accessibilityValue={{text: otpValue}}
              onChangeText={(text) => handleOTPChange(text)}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
              maxLength={6}
              textStyle={styles.textStyle}
              testID="qa_fld_OTP"
              error={!!otpError}
              errorMsg={otpError}
              outlineStyle={!!otpError && styles.errorOutlineStyle}
            />
            {otpError && <AVText style={styles.errorText}>{otpError}</AVText>}
          </View>
          <View style={styles.resendOtpView}>
            <AVText
              style={styles.resentOtpDescription}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
            >
              {Localized.Labels.did_not_receive_a_code}
            </AVText>
            <AVTouchableOpacity onPress={handleResendCode}>
              <AVText
                style={[
                  styles.resentOtpDescription,
                  styles.resentOtpButtonText,
                  {color: getActiveButtonColor()},
                ]}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
              >
                {Localized.Buttons.resend_code}
              </AVText>
            </AVTouchableOpacity>
          </View>
        </View>
        <View>
          <RoundedAppButton
            buttonType={RoundedAppButtonType.Solid}
            buttonViewStyle={[
              getPickupLocationStyles().buttonView,
              {
                backgroundColor: getActiveButtonColor(),
              },
            ]}
            inActiveButtonViewStyle={[
              getPickupLocationStyles().buttonView,
              styles.inActiveButtonView,
            ]}
            buttonContainerStyle={[
              getPickupLocationStyles().containerStyle,
              styles.buttonContainer,
              Settings.isRefiveAnd365PayOnly() && styles.buttonContainerDefault,
            ]}
            accessibilityLabelValue={Localized.Labels.verify}
            onPress={handleVerify}
            titleText={Localized.Labels.verify}
            accessibilityHintValue={'button'}
            aria-label={Localized.Labels.verify}
            isDisabled={!isValidOtp}
            testID="qa_btn_Verify"
            textStyle={styles.buttonText}
            inActiveTextStyle={styles.buttonText}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
          />
        </View>
      </View>
    ),
    [otpValue, otpError, isValidOtp],
  );

  const closeConfirmationView = useMemo(
    () => (
      <View>
        <View style={styles.content}>
          <View style={styles.titleView}>
            <View style={styles.titleIcon}>
              <AlertCircleExclamation width={32} height={31} />
            </View>
            <AVText
              style={styles.headerText}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
            >
              {Localized.Labels.are_you_sure_you_want_to_leave}
            </AVText>
          </View>
          <AVText
            style={styles.descriptionText}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
          >
            {Localized.Labels.you_have_not_finished_verifying_your_phone_number}
          </AVText>
        </View>
        <View style={styles.closeViewButtonParentView}>
          <RoundedAppButton
            buttonType={RoundedAppButtonType.Outline}
            buttonViewStyle={[
              getPickupLocationStyles().buttonView,
              {
                borderColor: getActiveButtonColor(),
              },
              styles.leaveButtonView,
              Settings.isRefiveAnd365PayOnly() && styles.leaveButtonViewDefault,
            ]}
            buttonContainerStyle={[
              getPickupLocationStyles().containerStyle,
              styles.leaveButtonContainer,
              Settings.isRefiveAnd365PayOnly() &&
                styles.leaveButtonContainerDefault,
            ]}
            accessibilityLabelValue={Localized.Buttons.leave}
            onPress={() => {
              handleLeave();
            }}
            titleText={Localized.Buttons.leave}
            accessibilityHintValue={'button'}
            aria-label={Localized.Buttons.leave}
            testID="qa_btn_Leave"
            textStyle={styles.buttonText}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
          />
          <RoundedAppButton
            buttonType={RoundedAppButtonType.Solid}
            buttonViewStyle={[
              getPickupLocationStyles().buttonView,
              {
                backgroundColor: getActiveButtonColor(),
              },
              styles.leaveButtonView,
              Settings.isRefiveAnd365PayOnly() &&
                styles.stayVerifyButtonViewDefault,
            ]}
            buttonContainerStyle={[
              getPickupLocationStyles().containerStyle,
              styles.stayVerifyButtonContainer,
            ]}
            accessibilityLabelValue={Localized.Buttons.stay_and_verify}
            onPress={() => {
              handleStayAndVerify();
            }}
            titleText={Localized.Buttons.stay_and_verify}
            accessibilityHintValue={'button'}
            aria-label={Localized.Buttons.stay_and_verify}
            testID="qa_btn_StayAndVerify"
            textStyle={styles.buttonText}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
          />
        </View>
      </View>
    ),
    [],
  );

  const verificationSuccess = useMemo(
    () => (
      <View>
        <View style={styles.content}>
          <View style={styles.titleView}>
            <View style={styles.titleIcon}>
              <SuccessTickIcon color={Styles.Colors.PayNew.success} />
            </View>
            <AVText
              style={styles.headerText}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
            >
              {Localized.Labels.phone_verified}
            </AVText>
          </View>
          <AVText
            style={styles.descriptionText}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
          >
            {
              Localized.Labels
                .your_phone_number_has_been_successfully_added_to_your_account
            }
          </AVText>
        </View>
        <View>
          <RoundedAppButton
            buttonType={RoundedAppButtonType.Solid}
            buttonViewStyle={[
              getPickupLocationStyles().buttonView,
              {
                backgroundColor: getActiveButtonColor(),
              },
            ]}
            buttonContainerStyle={[
              getPickupLocationStyles().containerStyle,
              styles.buttonContainer,
              Settings.isRefiveAnd365PayOnly() && styles.buttonContainerDefault,
            ]}
            accessibilityLabelValue={Localized.Buttons.continue_to_menu}
            onPress={() => {
              handleDone();
            }}
            titleText={Localized.Buttons.done}
            accessibilityHintValue={'button'}
            aria-label={Localized.Buttons.done}
            testID="qa_btn_Done"
            textStyle={styles.buttonText}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
          />
        </View>
      </View>
    ),
    [],
  );

  const getHeight = useMemo(() => {
    if (contentType === ContentTypes.VERIFY_PHONE_NUMBER) {
      return 320;
    } else if (contentType === ContentTypes.VERIFICATION_SUCCESS) {
      return 220;
    } else {
      return 230;
    }
  }, [contentType]);

  const renderBottomSheet = () => (
    <BottomSheet
      ref={bottomSheetRef}
      snapPoints={[getHeight + PixelRatio.getFontScale() * 35 + keyBoardHeight]}
      enableHandlePanningGesture={true}
      onChange={handleSheetChange}
      backdropComponent={renderBackdropComponent}
      style={styles.bottomSheet}
      enablePanDownToClose={!(contentType === ContentTypes.CLOSE_CONFIRMATION)}
      onAnimate={() => handleClose}
    >
      <BottomSheetScrollView>{renderContent()}</BottomSheetScrollView>
    </BottomSheet>
  );

  const renderModal = () => (
    <Modal
      animationType="fade"
      transparent
      visible={modalVisible}
      onRequestClose={handleClose}
    >
      <View style={styles.modalBackdrop}>
        <View style={styles.modalContainer}>
          <AVTouchableOpacity
            aria-label={`X icon, Double tap to ${Localized.Buttons.cancel} verify phone details`}
            role="button"
            style={styles.closeModalContainer}
            onPress={handleClose}
            accessible
            accessibilityLabel={Localized.Buttons.cancel}
            accessibilityRole="button"
            hitSlop={Styles.HitSlop_10}
          >
            <View style={styles.closeModalTitleContainer}>
              <CancelIcon />
            </View>
          </AVTouchableOpacity>
          {renderContent()}
        </View>
      </View>
    </Modal>
  );

  return (
    <View style={styles.container}>
      <Pressable style={styles.backdropPressable} onPress={handleClose} />
      {Platform.OS === 'web' ? renderModal() : renderBottomSheet()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Styles.bottomTreyBackgroundColor,
  },
  backdropPressable: {
    width: '100%',
    minHeight: '60%',
    backgroundColor: 'transparent',
  },
  bottomSheet: {
    borderRadius: Styles.Spacing.m4,
    overflow: 'hidden',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: Styles.Spacing.m3,
  },
  modalContainer: {
    width: '100%',
    maxWidth: 600,
    backgroundColor: Styles.Colors.PayNew.white01,
    borderRadius: Styles.Spacing.m3 + Styles.Spacing.m2,
    overflow: 'hidden',
    padding: Styles.Spacing.m3 + Styles.Spacing.m2,
    paddingBottom: Styles.Spacing.m4,
  },
  closeModalContainer: {
    position: 'absolute',
    top: Styles.Spacing.m1,
    right: Styles.Spacing.m1,
    width: Styles.Heights.touchTargetHeight2,
    height: Styles.Heights.touchTargetHeight2,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  closeModalTitleContainer: {
    width: Styles.Spacing.m2 + Styles.Spacing.m3,
    height: Styles.Spacing.m2 + Styles.Spacing.m3,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    marginHorizontal: Styles.Spacing.m4 - Styles.Spacing.m2,
    marginVertical: Styles.Spacing.m4 - Styles.Spacing.m2,
  },
  titleView: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  titleIcon: {
    marginLeft: -Styles.Spacing.m2,
    marginRight: Styles.Spacing.m2,
  },
  headerText: {
    fontSize: Styles.Fonts.f1,
    fontWeight: '700',
    color: Styles.black,
    textAlign: 'left',
  },
  descriptionText: {
    fontSize: Styles.Fonts.f1,
    fontWeight: '400',
    color: Styles.Colors.PayNew.black01,
    textAlign: 'left',
    marginRight: Styles.Spacing.m1,
    marginTop: Styles.Spacing.m3,
  },
  inputContainer: {
    marginBottom: 16,
  },
  resendOtpView: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: Styles.Spacing.m3,
  },
  resentOtpDescription: {
    fontSize: Styles.Fonts.f1,
    fontWeight: '400',
    color: Styles.Colors.PayNew.black01,
    textAlign: 'left',
    marginRight: Styles.Spacing.m2,
  },
  resentOtpButtonText: {
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
  buttonText: {
    fontSize: Styles.Fonts.f1,
    textTransform: 'none',
  },
  inActiveButtonView: {
    backgroundColor: Styles.Colors.PayNew.darkHuesBase04,
  },
  buttonContainer: {
    marginTop: Styles.Spacing.m0,
    alignSelf: 'flex-end',
  },
  buttonContainerDefault: {
    paddingRight: Styles.Spacing.m3,
  },
  closeViewButtonParentView: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  leaveButtonView: {
    flex: 1,
    width: '100%',
  },
  leaveButtonContainer: {
    marginTop: Styles.Spacing.m0,
    flex: 1,
    paddingLeft: Styles.Spacing.m3,
    paddingRight: Styles.Spacing.m1,
  },
  leaveButtonContainerDefault: {
    paddingRight: Styles.Spacing.m1,
  },
  leaveButtonViewDefault: {
    width: 98,
    alignSelf: 'flex-start',
    marginLeft: Styles.Spacing.m2,
  },
  stayVerifyButtonContainer: {
    marginTop: Styles.Spacing.m0,
    flex: 1,
    paddingLeft: Styles.Spacing.m1,
    paddingRight: Styles.Spacing.m3 + Styles.Spacing.m1,
  },
  stayVerifyButtonViewDefault: {
    width: 160,
    alignSelf: 'flex-end',
    marginRight: Styles.Spacing.m1,
  },
  errorText: {
    color: Styles.Colors.PayNew.error,
    fontSize: Styles.Fonts.f1,
    textAlign: 'left',
    fontWeight: '400',
    marginTop: Styles.Spacing.m2,
    marginBottom: Styles.Spacing.m1,
  },
  textStyle: {
    color: Styles.Colors.PayNew.black01,
    textAlign: 'left',
    fontSize: Styles.Fonts.f7,
    fontWeight: '400',
    marginLeft: -2,
  },
  errorOutlineStyle: {
    borderColor: Styles.Colors.PayNew.error,
  },
});

export default withForwardedNavigationParams()(
  withGlobalize(VerifyPhoneNumber),
);
